import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';
import { useNavigate } from 'react-router-dom';
import { FlexPlanEditor } from './FlexPlanEditor';
import { monthStartOf, seedDatesFromWeekdays, splitFlexPricing } from '../utils/flexPlan';
import { persistFirstMonthPlan } from '../services/flexPlans';
import { parseCron } from '../types/program';
import { type BrandKey } from '../config/brands';
import { MY_WAITLIST_KEY, PROGRAM_CAPACITY_KEY } from '../hooks/useProgramSeats';
import { isProgramFullError } from '../services/programWaitlist';


interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  active: boolean;
  metadata?: {
    bhcs_waiver?: unknown;
  } | null;
}

interface EnrolledStudent {
  student_id: string;
}

interface StudentParent {
  students: Student[];
}

interface ProgramLevels {
  levels: string[] | null;
}

interface Props {
  programId: string;
  selectedStudentId: string | null;
  enrollmentStartDate: string;
  enrollmentEndDate: string;
  selectedDays?: string[]; // For per_day billing
  billingType?: string; // To validate per_day enrollment
  optedOutItems: string[];
  /**
   * Every optional `line_item_config` id the program offered at this moment. With
   * `optedOutItems` it yields the positive selection, which is stored as `opt_in_items`
   * so the choice survives a later change to the program's pricing. Omit and only
   * nothing is recorded, and no optional row is charged.
   */
  optionalItemIds?: string[];
  onSuccess: () => void;
  sessionId: string;
  programName?: string;
  programType?: string;
  programSchedule?: string;
  /** Raw cron (e.g. "0 15 * * 1,3,5"); used to seed the flex first-month plan weekdays. */
  scheduleCron?: string;
  /**
   * The program's brand, for the first-month Flex calendar's closure greying. Absent reads as
   * the default brand, which is what every Flex program is today.
   */
  brandKey?: BrandKey;
  /**
   * When provided, the enrollment will be created on behalf of this parent.
   * Also used to scope the student list to this parent's students.
   * If not provided, the currently authenticated parent's account is used.
   */
  overrideParentId?: string;
  /** If true, submission is gated on having a waiver signature */
  requireWaiver?: boolean;
  /** Waiver signature captured from parent UI */
  waiverSignature?: { name: string; date: string };
  /** Optional: pass available levels from parent container to control UI placement */
  availableLevels?: string[];
  /** Optional: selected level provided by parent container (hides internal selector) */
  selectedLevel?: string;
  /** Optional: submit as multiple enrollments (one per selected program/session) */
  multiProgramSelections?: MultiProgramSelection[];
  /**
   * Which portal this form is mounted in, used to attribute its analytics events.
   *
   * The form is shared: a parent enrolling their own child renders it from `ProgramDetails`,
   * an admin enrolling on someone's behalf renders it from `AdminPrograms`. Without this the
   * events default to `user_type='parent'` and every admin enrollment is counted as parent
   * self-service in the funnel. Passed explicitly rather than inferred from
   * `overrideParentId`, which is undefined until the admin picks a parent.
   */
  actorType?: 'parent' | 'admin';
}

interface MultiProgramSelection {
  programId: string;
  enrollmentStartDate: string;
  enrollmentEndDate: string;
  optedOutItems: string[];
  /** See `optionalItemIds` on Props — the optional ids this program offered. */
  optionalItemIds?: string[];
  selectedDays?: string[];
  metadata?: {
    camp_lunch_selected?: boolean;
  };
}

type EnrollmentInsertRow = {
  program_id: string;
  student_id: string;
  enrolled_by: string;
  enrollment_start_date: string;
  enrollment_end_date: string;
  metadata: Record<string, unknown>;
};

type EnrollmentInsertResult = { id: string } | { id: string }[];

/** The program filled before this enrollment was written (BHCS-124). */
class ProgramFullError extends Error {}

type ExistingEnrollment = {
  id: string;
  program_id: string;
  is_active: boolean;
};

export function EnrollStudent({ programId, selectedStudentId, enrollmentStartDate, enrollmentEndDate, selectedDays, billingType, optedOutItems, optionalItemIds, onSuccess, sessionId, programName, scheduleCron, brandKey, overrideParentId, requireWaiver, waiverSignature, availableLevels, selectedLevel: selectedLevelProp, multiProgramSelections, actorType = 'parent' }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Admin-on-behalf only: suppress the auto-generated first invoice email so the
  // admin can review and send it later (parity with the CLI/MCP suppress_email flag).
  const [suppressInvoiceEmail, setSuppressInvoiceEmail] = useState(false);
  const navigate = useNavigate();
  // Level is controlled by parent container via selectedLevel prop

  // Fetch program levels when not provided by parent container (parent flow)
  const { data: programLevels } = useQuery<ProgramLevels | null>({
    queryKey: ['program-levels', programId],
    queryFn: async () => {
      if (!programId) return null;
      if (availableLevels && Array.isArray(availableLevels)) {
        return { levels: availableLevels } as ProgramLevels;
      }
      const { data, error } = await supabase
        .from('programs')
        .select('levels')
        .eq('id', programId)
        .single();
      if (error) throw error;
      return data as ProgramLevels;
    }
  });

  // ===== Flex monthly: first-month plan (only when billingType === 'flex_monthly') =====
  const isFlex = billingType === 'flex_monthly';
  const flexPlanMonth = enrollmentStartDate ? monthStartOf(enrollmentStartDate) : '';
  const { data: flexPricing } = useQuery<{ rows: Array<{ id?: string; amount: number; type?: string | null; subtype?: string | null; isOptional?: boolean }>; weekdays: number[] }>({
    queryKey: ['flex-enroll-pricing', programId, scheduleCron],
    enabled: isFlex && !!programId,
    queryFn: async () => {
      const { data } = await supabase
        .from('pricing')
        .select('amount, is_optional, line_item_configs:line_item_config_id ( id, type, subtype )')
        .eq('program_id', programId);
      const rows = ((data ?? []) as Array<{ amount: number; is_optional?: boolean; line_item_configs: { id?: string; type?: string; subtype?: string | null } | { id?: string; type?: string; subtype?: string | null }[] }>).map((row) => {
        const cfg = Array.isArray(row.line_item_configs) ? row.line_item_configs[0] : row.line_item_configs;
        return { id: cfg?.id, amount: row.amount, type: cfg?.type, subtype: cfg?.subtype, isOptional: row.is_optional === true };
      });
      const weekdays = (parseCron(scheduleCron || '').days || []).map((d) => d % 7);
      return { rows, weekdays };
    },
  });
  // First-month estimate honors the family's add-on choice and includes one-time materials
  // (the first invoice bills tuition + per-day fees × days + materials once + flat fees).
  // The selection here is the one being made on screen, so it is derived from what this
  // program is offering right now rather than from anything stored.
  const flexFirstMonth = splitFlexPricing(flexPricing?.rows ?? [], {
    includeMaterials: true,
    optIn: optionalItemIds
      ? optionalItemIds.filter((id) => !(optedOutItems ?? []).includes(id))
      : null,
  });
  const [flexDates, setFlexDates] = useState<string[]>([]);
  // Seed the first-month editor once from the program's weekly schedule.
  const [flexSeededFor, setFlexSeededFor] = useState<string>('');
  if (isFlex && flexPricing && flexPlanMonth && flexSeededFor !== flexPlanMonth) {
    setFlexSeededFor(flexPlanMonth);
    setFlexDates(seedDatesFromWeekdays(flexPlanMonth, flexPricing.weekdays).filter((d) => d >= enrollmentStartDate));
  }

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ studentId, startDate, endDate }: { studentId: string; startDate: string; endDate:string }) => {

      if (!programId || !studentId) {
        throw new Error('Missing required fields');
      }

      // Determine the parent ID for the enrolled_by field
      let parentIdForEnrollment: string;
      if (overrideParentId) {
        parentIdForEnrollment = overrideParentId;
      } else {
        if (!user?.email) {
          throw new Error('No authenticated user');
        }
        const { data: parent, error: parentError } = await supabase
          .from('parents')
          .select('id')
          .eq('email', user.email)
          .single();
        if (parentError || !parent) {
          throw new Error('Parent not found');
        }
        parentIdForEnrollment = parent.id;
      }

      const baseMetadata: Record<string, unknown> = {
        ...(requireWaiver && waiverSignature?.name && waiverSignature?.date
          ? { signed_waiver: { name: waiverSignature.name, date: waiverSignature.date } }
          : {}),
        ...((effectiveSelectedLevel && effectiveSelectedLevel.trim()) ? { level: effectiveSelectedLevel } : {}),
      };

      // Record what the family picked, and only that. Written only when the caller tells us
      // which ids were on offer, because the positive set is not knowable otherwise.
      const choiceMetadata = (
        offered: string[] | undefined,
        optedOut: string[] | undefined,
      ): Record<string, unknown> => {
        const out = optedOut ?? [];
        // Only the positive selection is stored. Writing the complement as well is what let
        // a later pricing change re-interpret an existing enrollment.
        return Array.isArray(offered)
          ? { opt_in_items: offered.filter((id) => !out.includes(id)) }
          : {};
      };

      const hasMultiProgramSelections =
        Array.isArray(multiProgramSelections) && multiProgramSelections.length > 0;

      const enrollmentRows: EnrollmentInsertRow[] = hasMultiProgramSelections
        ? multiProgramSelections.map((selection) => ({
            program_id: selection.programId,
            student_id: studentId,
            enrolled_by: parentIdForEnrollment,
            enrollment_start_date: selection.enrollmentStartDate,
            enrollment_end_date: selection.enrollmentEndDate,
            metadata: {
              ...baseMetadata,
              ...(selection.selectedDays && selection.selectedDays.length > 0 ? { selected_days: selection.selectedDays } : {}),
              ...choiceMetadata(selection.optionalItemIds, selection.optedOutItems),
              ...(selection.metadata ?? {}),
            },
          }))
        : [
            {
              program_id: programId,
              student_id: studentId,
              enrolled_by: parentIdForEnrollment,
              enrollment_start_date: startDate,
              enrollment_end_date: endDate,
              metadata: {
                ...baseMetadata,
                ...(selectedDays && selectedDays.length > 0 ? { selected_days: selectedDays } : {}),
                ...choiceMetadata(optionalItemIds, optedOutItems),
              }
            }
          ];

      const createdIds: { id: string }[] = [];

      const { data: inserted, error: insertError } = await supabase
        .from('enrollments')
        .insert(enrollmentRows)
        .select('id');

      if (!insertError) {
        createdIds.push(...((inserted ?? []) as { id: string }[]));
      } else if (isProgramFullError(insertError)) {
        // enforce_program_capacity: the last seat went while the form was open. Its message names
        // the program (which week, in a camp bundle) and points at the waitlist.
        throw new ProgramFullError(insertError.message);
      } else if (insertError.code === '23505') {
        // unique_student_program rejected the batch (a multi-row INSERT is all-or-nothing, so
        // nothing was written). An admin/CLI unenroll soft-deactivates the row rather than
        // deleting it, so the student may still own a dormant row for this program — re-enroll
        // has to revive that row instead. Rows that are still active are a genuine duplicate.
        const { data: existingRows, error: existingError } = await supabase
          .from('enrollments')
          .select('id, program_id, is_active')
          .eq('student_id', studentId)
          .in('program_id', enrollmentRows.map((row) => row.program_id));

        if (existingError) throw existingError;

        const existingByProgram = new Map<string, ExistingEnrollment>(
          ((existingRows ?? []) as ExistingEnrollment[]).map((row) => [row.program_id, row])
        );

        if (enrollmentRows.some((row) => existingByProgram.get(row.program_id)?.is_active)) {
          throw new Error('This student is already enrolled in this program.');
        }

        // Reviving restores is_active along with the new enrollment's dates and metadata:
        // unenroll stamps enrollment_end_date to the unenroll day, which would otherwise leave
        // the row ending before it starts. The row keeps its existing invoices, and the AFTER
        // INSERT billing trigger does not re-run, so any billing adjustment is an admin action.
        for (const row of enrollmentRows) {
          const existing = existingByProgram.get(row.program_id);
          if (!existing) continue;
          const { data: revived, error: reviveError } = await supabase
            .from('enrollments')
            .update({
              enrolled_by: row.enrolled_by,
              enrollment_start_date: row.enrollment_start_date,
              enrollment_end_date: row.enrollment_end_date,
              metadata: row.metadata,
              is_active: true,
            })
            .eq('id', existing.id)
            .select('id')
            .single();
          if (reviveError) throw isProgramFullError(reviveError) ? new ProgramFullError(reviveError.message) : reviveError;
          createdIds.push(revived as { id: string });
        }

        const rowsToInsert = enrollmentRows.filter((row) => !existingByProgram.has(row.program_id));
        if (rowsToInsert.length > 0) {
          const { data: reinserted, error: reinsertError } = await supabase
            .from('enrollments')
            .insert(rowsToInsert)
            .select('id');
          if (reinsertError) throw isProgramFullError(reinsertError) ? new ProgramFullError(reinsertError.message) : reinsertError;
          createdIds.push(...((reinserted ?? []) as { id: string }[]));
        }
      } else {
        throw insertError;
      }

      if (createdIds.length === 0) {
        throw new Error('Enrollment failed: no enrollment record was created.');
      }

      const data: EnrollmentInsertResult = hasMultiProgramSelections ? createdIds : createdIds[0];

      // Admin-on-behalf: the enrollment AFTER INSERT trigger has already created the
      // first invoice synchronously. Flag it suppressed before the db-webhook's 3s wait
      // elapses so no invoice-created email goes out. Mirrors cli-api handleEnroll.
      if (overrideParentId && suppressInvoiceEmail) {
        const result = data as EnrollmentInsertResult;
        const enrollmentIds = (Array.isArray(result) ? result : [result])
          .map((row) => row.id)
          .filter(Boolean);
        if (enrollmentIds.length > 0) {
          const { error: suppressError } = await supabase
            .from('invoices')
            .update({ email_suppressed: true })
            .in('enrollment_id', enrollmentIds);
          if (suppressError) throw suppressError;
        }
      }

      // Flex monthly: the enrollment trigger does NOT create the first invoice (guarded).
      // The RPC persists the confirmed first-month plan AND bills it from those dates,
      // server-side and atomically (also bills one-time materials).
      if (isFlex && !hasMultiProgramSelections) {
        const enrollmentId = (data as { id: string }).id;
        const planMonth = monthStartOf(startDate);
        await persistFirstMonthPlan(enrollmentId, planMonth, flexDates, {
          suppressEmail: Boolean(overrideParentId && suppressInvoiceEmail),
        });
      }

      return data as EnrollmentInsertResult;
    },
    onSuccess: async (data, variables) => {
      // Invalidate queries to refetch program data
      const selectedProgramIds = Array.isArray(multiProgramSelections) && multiProgramSelections.length > 0
        ? multiProgramSelections.map((selection) => selection.programId)
        : [programId];
      selectedProgramIds.forEach((selectedProgramId) => {
        queryClient.invalidateQueries({ queryKey: ['program', selectedProgramId] });
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      // A seat was taken, and a waitlisted child who enrolled has left the line.
      queryClient.invalidateQueries({ queryKey: [PROGRAM_CAPACITY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_WAITLIST_KEY] });

      // Track successful enrollment
      if (user && data) {
        const enrollmentId = Array.isArray(data) ? data[0]?.id : data.id;
        const enrolledStudent = (students || []).find(s => s.id === variables.studentId);
        const studentName = enrolledStudent
          ? `${enrolledStudent.first_name} ${enrolledStudent.last_name}`.trim()
          : undefined;
        trackEvent({
          eventType: 'program_enroll_complete',
          user: user,
          userType: actorType,
          entityType: 'enrollment',
          entityId: enrollmentId,
          sessionId: sessionId,
          metadata: {
            program_id: programId,
            program_name: programName,
            student_id: variables.studentId,
            student_name: studentName,
            enrollment_count: Array.isArray(data) ? data.length : 1,
            on_behalf_of_parent_id: overrideParentId ?? null,
          },
          email: user.email,
        });
      }

      onSuccess();
      // Navigate differently for admin-on-behalf vs parent flow
      if (overrideParentId) {
        navigate('/admin-app/enrollments');
      } else {
        const firstEnrollmentId = Array.isArray(data) ? data[0]?.id : data.id;
        if (firstEnrollmentId) {
          navigate(`/enrollments/${firstEnrollmentId}`);
        } else {
          navigate('/dashboard');
        }
      }
    },
    onError: (err: Error) => {
      setError(err.message || 'An unexpected error occurred.');
      // Refetch the counts so the program page shows it full and offers the waitlist.
      if (err instanceof ProgramFullError) {
        queryClient.invalidateQueries({ queryKey: [PROGRAM_CAPACITY_KEY] });
        queryClient.invalidateQueries({ queryKey: [MY_WAITLIST_KEY] });
      }
    },
  });

  const handleCancel = () => {
    if (user) {
      trackEvent({
        eventType: 'program_enroll_abandon',
        user: user,
        userType: actorType,
        entityType: 'program',
        entityId: programId,
        sessionId: sessionId,
        email: user.email,
      });
    }
    onSuccess();
  };

  // Fetch students for the parent (or override parent)
  const { data: students, isLoading } = useQuery<Student[]>({
    queryKey: ['parent-students', overrideParentId ?? user?.email],
    queryFn: async () => {
      let parentIdToUse: string | null = null;
      if (overrideParentId) {
        parentIdToUse = overrideParentId;
      } else {
        if (!user?.email) throw new Error('No authenticated user');
        const { data: parent } = await supabase
          .from('parents')
          .select('id')
          .eq('email', user.email)
          .single();
        if (!parent) throw new Error('Parent not found');
        parentIdToUse = parent.id;
      }
      const { data: studentParents } = await supabase
        .from('student_parents')
        .select('students (id, first_name, last_name, date_of_birth, active, metadata)')
        .eq('parent_id', parentIdToUse);
      return (studentParents || []).flatMap((sp: StudentParent) => sp.students);
    }
  });

  // Fetch students already enrolled in this program. Soft-unenrolled rows are excluded so a
  // previously-unenrolled student can be picked again (the mutation revives their row).
  const { data: enrolledStudentIds } = useQuery<string[]>({
    queryKey: ['enrolled-students', programId],
    queryFn: async () => {
      if (!programId) return [];
      const { data } = await supabase
        .from('enrollments')
        .select('student_id')
        .eq('program_id', programId)
        .eq('is_active', true);
      return (data || []).map((e: EnrolledStudent) => e.student_id);
    }
  });

  // Filter students to only those not already enrolled and are active
  const availableStudents = students?.filter(
    student => !enrolledStudentIds?.includes(student.id) && student.active
  );

  // Only return after all hooks are declared
  if (availableStudents && availableStudents.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded text-center text-gray-500">
        All your children are already enrolled in this program.
      </div>
    );
  }

  const hasLevels = Array.isArray(availableLevels) && availableLevels.length > 0
    ? true
    : Array.isArray(programLevels?.levels) && (programLevels?.levels?.length || 0) > 0;
  // Enroll is blocked if selected student has neither signed nor opted out waiver
  const selectedStudent = (students || []).find(s => s.id === selectedStudentId);
  const selectedStudentWaiver = selectedStudent && selectedStudent.metadata && (selectedStudent.metadata as { bhcs_waiver?: unknown }).bhcs_waiver;
  const signatureProvided = (waiverSignature?.name && waiverSignature.name.trim()) && (waiverSignature?.date && waiverSignature.date.trim());
  // Satisfied if student already has a waiver on file; otherwise, when program requires a waiver, a fresh signature satisfies.
  const hasWaiverOnFile = selectedStudentWaiver != null;
  const waiverSatisfied = hasWaiverOnFile || (requireWaiver && signatureProvided);
  // Global waiver requirement applies for parent flow (no overrideParentId).
  // Also honor per-program requireWaiver; missing either triggers the gate.
  const mustHaveWaiver = !overrideParentId || !!requireWaiver;
  const waiverRequiredAndMissing = mustHaveWaiver && !waiverSatisfied;
  const effectiveSelectedLevel = selectedLevelProp ?? '';
  const hasMultiSelectionPayload = Array.isArray(multiProgramSelections);
  const hasAtLeastOneMultiSelection = (multiProgramSelections?.length ?? 0) > 0;
  const requiresPerDaySelection = !multiProgramSelections || multiProgramSelections.length === 0;
  // No start date yet: a series billed by the class waits for the parent to pick the first class.
  const isMissingStartDate = !hasMultiSelectionPayload && billingType !== 'per_day' && !enrollmentStartDate;
  const isDisabled = isPending || isLoading || !selectedStudentId || isMissingStartDate || (hasMultiSelectionPayload && !hasAtLeastOneMultiSelection) || (requiresPerDaySelection && billingType === 'per_day' && (!selectedDays || selectedDays.length === 0)) || waiverRequiredAndMissing || (hasLevels && !effectiveSelectedLevel);
  const isPerDayWithNoDays = billingType === 'per_day' && (!selectedDays || selectedDays.length === 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      setError('Please select a student');
      return;
    }
    if (hasMultiSelectionPayload && !hasAtLeastOneMultiSelection) {
      setError('Please select at least one weekly session');
      return;
    }
    if (isMissingStartDate) {
      setError('Please choose the first class');
      return;
    }
    if (requiresPerDaySelection && billingType === 'per_day' && (!selectedDays || selectedDays.length === 0)) {
      setError('Please select at least one day to attend');
      return;
    }
    if (hasLevels && !effectiveSelectedLevel) {
      setError('Please select a level');
      return;
    }
    mutate({ studentId: selectedStudentId, startDate: enrollmentStartDate, endDate: enrollmentEndDate });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      {error && <div className="text-red-600 text-sm">{error}</div>}
      {/* Flex monthly: confirm the first month's days; the first invoice bills these. */}
      {isFlex && selectedStudentId && flexPlanMonth && (
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-700">First month&apos;s plan</p>
          <p className="text-xs text-gray-500">
            Pick the days for the first month. The first invoice bills these days × the per-day
            rate. You can adjust later months from the enrollment&apos;s plan editor.
          </p>
          <FlexPlanEditor
            planMonth={flexPlanMonth}
            perDayRate={flexFirstMonth.perDayRate}
            dates={flexDates}
            onChange={setFlexDates}
            monthlyAddOns={flexFirstMonth.monthlyAddOns}
            addOnsLabel="first-month fees"
            enrollmentStartYmd={enrollmentStartDate}
            scheduleWeekdays={flexPricing?.weekdays ?? []}
            brandKey={brandKey}
          />
        </div>
      )}
      {/* Level is selected by parent container; no internal selector here */}
      {overrideParentId && (
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={suppressInvoiceEmail}
            onChange={(e) => setSuppressInvoiceEmail(e.target.checked)}
            disabled={isPending}
          />
          <span>
            Don't email the invoice to the parent
            <span className="block text-xs text-gray-500">
              The invoice is still created. You can send it later from Finances.
            </span>
          </span>
        </label>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleCancel}
          className="flex-1 bg-white text-gray-700 border border-gray-300 py-2 rounded hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`flex-1 py-2 rounded transition-colors ${
            isDisabled 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          disabled={isDisabled}
        >
          {isPending ? 'Enrolling...' : 'Enroll Student'}
        </button>
      </div>
      {waiverRequiredAndMissing && selectedStudentId && (
        <div className="flex items-center justify-between gap-3 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
          <p className="text-sm text-amber-700">
            This student must have a signed waiver on file before enrolling.
          </p>
          <button
            type="button"
            onClick={() => navigate('/student-waiver')}
            className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Sign Waiver
          </button>
        </div>
      )}
      {isMissingStartDate && selectedStudentId && (
        <p className="text-sm text-amber-700 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
          Choose the first class above to continue.
        </p>
      )}
      {isPerDayWithNoDays && selectedStudentId && (
        <div className="text-sm text-gray-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-amber-700">
                Please select at least one day above to proceed with enrollment.
              </p>
            </div>
          </div>
        </div>
      )}
    </form>
  );
} 